// Network and contract configuration.
//
// Everything that points at infrastructure you own comes from REACT_APP_*
// environment variables (see .env.example). CRA only inlines variables with
// that prefix, so plain process.env.X overrides would silently be undefined.
//
// Ref Finance's own contracts, pool ids and backend services are deliberately
// absent: their pool ids and routes do not exist on your exchange contract.
// An empty value means "not deployed" and switches the feature off (FEATURES).

type NearEnv = 'testnet' | 'mainnet';

const env = (name: string, fallback = ''): string =>
  (process.env[`REACT_APP_${name}`] || fallback).trim();

const envList = (name: string): string[] =>
  env(name)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

/** A pool with no hook is routable. A hooked pool is routable only when its account is listed. */
export function univ4HookAllowed(hooks: string | null | undefined): boolean {
  if (!hooks) return true;
  return envList('UNIV4_HOOK_ALLOWLIST').includes(hooks);
}

export function getNearEnv(
  nearEnv: string = process.env.REACT_APP_NEAR_ENV
): NearEnv {
  return nearEnv === 'mainnet' || nearEnv === 'production'
    ? 'mainnet'
    : 'testnet';
}

const RPC_DEFAULTS: Record<NearEnv, Record<string, any>> = {
  mainnet: {
    defaultRpc: {
      url: 'https://free.rpc.fastnear.com',
      simpleName: 'fastnear rpc',
    },
    officialRpc: {
      url: 'https://rpc.mainnet.near.org',
      simpleName: 'official rpc',
    },
    lavaRpc: { url: 'https://near.lava.build', simpleName: 'lava rpc' },
  },
  testnet: {
    defaultRpc: {
      url: 'https://rpc.testnet.fastnear.com',
      simpleName: 'fastnear rpc',
    },
  },
};

export function getExtendConfig(
  nearEnv: string = process.env.REACT_APP_NEAR_ENV
) {
  const RPC_LIST = { ...RPC_DEFAULTS[getNearEnv(nearEnv)] };
  if (env('RPC_URL')) {
    RPC_LIST.defaultRpc = { url: env('RPC_URL'), simpleName: 'custom rpc' };
  } else if (env('RPC_PROXY') && typeof window !== 'undefined') {
    // Same-origin proxy. Nginx adds the FastNear API key, so the browser never sees it.
    RPC_LIST.defaultRpc = {
      url: `${window.location.origin}/near-rpc`,
      simpleName: 'fastnear rpc',
    };
  }
  return {
    RPC_LIST,
    pool_protocol: env('INDEXER_URL') ? 'indexer' : 'rpc',
  };
}

export function getCustomConfig() {
  let customRpcMapStr;
  try {
    customRpcMapStr = window.localStorage.getItem('customRpcList');
  } catch (error) {}

  let customRpcMap = {};
  if (customRpcMapStr) {
    try {
      customRpcMap = JSON.parse(customRpcMapStr);
    } catch (error) {}
  }
  return customRpcMap;
}

interface NetworkConfig {
  networkId: string;
  walletUrl: string;
  myNearWalletUrl: string;
  helperUrl: string;
  explorerUrl: string;
  pikespeakUrl: string;
  nearExplorerUrl: string;
  txIdApiUrl: string;
  kitWalletOn?: boolean;
  WRAP_NEAR_CONTRACT_ID: string;
  USDC_TOKEN_ID: string;
  USDT_TOKEN_ID: string;
  USDCe_TOKEN_ID: string;
  USDTe_TOKEN_ID: string;
}

const NETWORK: Record<NearEnv, NetworkConfig> = {
  mainnet: {
    networkId: 'mainnet',
    walletUrl: 'https://wallet.near.org',
    myNearWalletUrl: 'https://app.mynearwallet.com/',
    helperUrl: 'https://api.kitwallet.app',
    explorerUrl: 'https://nearblocks.io',
    pikespeakUrl: 'https://pikespeak.ai',
    nearExplorerUrl: 'https://explorer.near.org/',
    txIdApiUrl: 'https://api3.nearblocks.io',
    WRAP_NEAR_CONTRACT_ID: 'wrap.near',
    USDC_TOKEN_ID:
      '17208628f84f5d6ad33f0da3bbbeb27ffcb398eac501a31bd6ad2011e36133a1',
    USDT_TOKEN_ID: 'usdt.tether-token.near',
    USDCe_TOKEN_ID:
      'a0b86991c6218b36c1d19d4a2e9eb0ce3606eb48.factory.bridge.near',
    USDTe_TOKEN_ID:
      'dac17f958d2ee523a2206206994597c13d831ec7.factory.bridge.near',
  },
  testnet: {
    networkId: 'testnet',
    walletUrl: 'https://wallet.testnet.near.org',
    myNearWalletUrl: 'https://testnet.mynearwallet.com/',
    helperUrl: 'https://testnet-api.kitwallet.app',
    explorerUrl: 'https://testnet.nearblocks.io',
    pikespeakUrl: 'https://pikespeak.ai',
    nearExplorerUrl: 'https://explorer.testnet.near.org/',
    txIdApiUrl: 'https://api-testnet.nearblocks.io',
    kitWalletOn: true,
    WRAP_NEAR_CONTRACT_ID: 'wrap.testnet',
    USDC_TOKEN_ID: 'usdc.fakes.testnet',
    USDT_TOKEN_ID: 'usdt.fakes.testnet',
    USDCe_TOKEN_ID: 'usdc.fakes.testnet',
    USDTe_TOKEN_ID: 'usdt.fakes.testnet',
  },
};

export default function getConfig(
  nearEnv: string = process.env.REACT_APP_NEAR_ENV
) {
  const network = getNearEnv(nearEnv);
  const RPC_LIST = Object.assign(
    getExtendConfig(network).RPC_LIST,
    getCustomConfig()
  ) as any;
  let endPoint = 'defaultRpc';
  try {
    endPoint = window.localStorage.getItem('endPoint') || endPoint;
    if (!RPC_LIST[endPoint]) {
      endPoint = 'defaultRpc';
      localStorage.removeItem('endPoint');
    }
  } catch (error) {}

  return {
    ...NETWORK[network],
    nodeUrl: RPC_LIST[endPoint].url,

    // ---- your deployment ----
    REF_FI_CONTRACT_ID: env(
      'EXCHANGE_CONTRACT_ID',
      network === 'mainnet' ? 'exchange.zear-dex.near' : ''
    ),
    WRAP_NEAR_CONTRACT_ID: env(
      'WRAP_NEAR_CONTRACT_ID',
      NETWORK[network].WRAP_NEAR_CONTRACT_ID
    ),
    REF_TOKEN_ID: env('PROTOCOL_TOKEN_ID'),

    // ---- optional services; empty = feature off ----
    indexerUrl: env('INDEXER_URL'),
    sodakiApiUrl: env('DATA_SERVICE_URL'),
    dataServiceApiUrl: env('DATA_SERVICE_URL'),
    memeRankApiUrl: '',
    smartRouterUrl: env('SMART_ROUTER_URL'),

    // ---- optional contracts; empty = not deployed ----
    REF_FARM_CONTRACT_ID: env('FARM_CONTRACT_ID'),
    REF_FARM_BOOST_CONTRACT_ID: env('BOOST_FARM_CONTRACT_ID'),
    REF_UNI_V3_SWAP_CONTRACT_ID: env('DCL_CONTRACT_ID'),
    REF_UNI_SWAP_CONTRACT_ID: env('DCL_CONTRACT_ID'),
    UNIV3_CONTRACT_ID: env('UNIV3_CONTRACT_ID'),
    UNIV4_CONTRACT_ID: env('UNIV4_CONTRACT_ID'),
    UNIV4_HOOK_ALLOWLIST: envList('UNIV4_HOOK_ALLOWLIST'),
    // Graduated launchpad tokens are added to the swap token list.
    ZEARFUN_CONTRACT_ID: env(
      'ZEARFUN_CONTRACT_ID',
      network === 'testnet' ? 'x88fun.testnet' : 'zear-fun.near'
    ),
    XREF_TOKEN_ID: '',
    REF_VE_CONTRACT_ID: '',
    REF_ADBOARD_CONTRACT_ID: '',
    REF_AIRDROP_CONTRACT_ID: '',
    REF_MEME_FARM_CONTRACT_ID: '',
    REF_TOKEN_LOCKER_CONTRACT_ID: env('TOKEN_LOCKER_CONTRACT_ID'),
    MEME_CHECK_IN_CONTRACT_ID: '',
    MEME_NFT_CONTRACT_ID: '',
    BURROW_CONTRACT_ID: '',
    BRRR_TOKEN_ID: '',

    // ---- pools ----
    // Classic pools need no configuration. Stable/rated pool ids below are the
    // hooks the upstream UI uses to special-case its own stable pools; they stay
    // empty until you create stable pools on your exchange.
    STABLE_POOL_ID: '',
    STABLE_POOL_IDS: [] as string[],
    STABLE_TOKEN_IDS: [] as string[],
    STABLE_TOKEN_INDEX: {},
    STABLE_POOL_USN_ID: '',
    STABLE_TOKEN_USN_IDS: [] as string[],
    STABLE_TOKEN_USN_INDEX: {},
    USN_ID: '',
    BTC_POOL_ID: '',
    BTCIDS: [] as string[],
    BTC_IDS_INDEX: {},
    USDTT_USDCC_USDT_USDC_POOL_ID: '',
    USDTT_USDCC_USDT_USDC_TOKEN_IDS: [] as string[],
    USDT_USDC_POOL_ID: '',
    USDT_USDC_TOKEN_IDS: [] as string[],
    FRAX_USDC_POOL_ID: '',
    FRAX_USDC_TOKEN_IDS: [] as string[],
    USDCW_POOL_ID: '',
    USDCW_TOKEN_IDS: [] as string[],
    Frax_SFrax_POOL_ID: '',
    Frax_SFrax_TOKEN_IDS: [] as string[],
    ZNEARnM_USDC_POOL_ID: '',
    ZNEARnM_USDC_TOKEN_IDS: [] as string[],

    BLACKLIST_POOL_IDS: envList('BLACKLIST_POOL_IDS'),
    BLACK_TOKEN_LIST: envList('BLACKLIST_TOKEN_IDS'),
    blackList: [] as string[],
    FARM_BLACK_LIST_V2: [] as string[],
    boostBlackList: [] as string[],
    DCL_POOL_BLACK_LIST: [] as string[],
    switch_on_dcl_farms: 'off',
    FARM_LOCK_SWITCH: 0,
    VotingGauge: ['10%', '10%'],

    TOP_POOLS_TOKEN_REFRESH_INTERVAL: 60,
    POOL_TOKEN_REFRESH_INTERVAL: 20,
    TOTAL_PLATFORM_FEE_REVENUE: '0',
    CUMULATIVE_REF_BUYBACK: '0',
  };
}

// Upstream special-cased its stable/rated pools by hardcoded id. Kept with
// empty values so the stable-pool code paths stay inert until configured.
export function getExtraStablePoolConfig(
  _nearEnv: string = process.env.REACT_APP_NEAR_ENV
) {
  return {
    BTCIDS: [] as string[],
    BTC_STABLE_POOL_INDEX: {},
    BTC_STABLE_POOL_ID: '',
    CUSDIDS: [] as string[],
    CUSD_STABLE_POOL_INDEX: {},
    CUSD_STABLE_POOL_ID: '',
    STNEAR_POOL_ID: '',
    LINEAR_POOL_ID: '',
    STNEARIDS: [] as string[],
    LINEARIDS: [] as string[],
    STNEAR_POOL_INDEX: {},
    LINEAR_POOL_INDEX: {},
    NEARX_POOL_ID: '',
    NEARXIDS: [] as string[],
    NEARX_POOL_INDEX: {},
    NEW_NEARX_POOL_ID: '',
    NEW_NEARXIDS: [] as string[],
    NEW_NEARX_POOL_INDEX: {},
    USDT_POOL_ID: '',
    USDTIDS: [] as string[],
    USDT_POOL_INDEX: {},
    RATED_POOLS_IDS: [] as string[],
    DEGEN_POOLS_IDS: [] as string[],
    USDTT_USDCC_USDT_USDC_POOL_INDEX: {},
    USDT_USDC_POOL_INDEX: {},
    FRAX_USDC_POOL_INDEX: {},
    USDCW_TOKEN_INDEX: {},
    Frax_SFrax_POOL_INDEX: {},
    ZNEARnM_USDC_POOL_INDEX: {},
  };
}

// Branding shown to wallets during sign-in.
export const APP_META = {
  name: env('APP_NAME', 'DEX'),
  url: env(
    'APP_URL',
    typeof window !== 'undefined' ? window.location.origin : ''
  ),
  logoUrl:
    env('APP_LOGO_URL') ||
    (typeof window !== 'undefined'
      ? `${window.location.origin}/brand/mark.png`
      : '/brand/mark.png'),
  docsUrl: env('DOCS_URL'),
  walletConnectProjectId: env('WALLETCONNECT_PROJECT_ID'),
};

// Which optional parts of the app have something to talk to.
export const FEATURES = (() => {
  const config = getConfig();
  return {
    indexer: !!config.indexerUrl,
    dataService: !!config.dataServiceApiUrl,
    smartRouter: !!config.smartRouterUrl,
    dcl: !!config.REF_UNI_V3_SWAP_CONTRACT_ID,
    // Stable/rated pools need their ids configured (see getExtraStablePoolConfig).
    stablePools:
      config.STABLE_POOL_IDS.length > 0 ||
      getExtraStablePoolConfig().RATED_POOLS_IDS.length > 0,
    farms: false,
    boostFarms: false,
    buyNear: !!env('TRANSAK_API_KEY'),
    walletConnect: !!env('WALLETCONNECT_PROJECT_ID'),
  };
})();

if (!getConfig().REF_FI_CONTRACT_ID) {
  // eslint-disable-next-line no-console
  console.error(
    'REACT_APP_EXCHANGE_CONTRACT_ID is not set - copy .env.example to .env.local'
  );
}

export function getTransakConfig(
  accountId: string,
  nearEnv: string = process.env.REACT_APP_NEAR_ENV
) {
  return {
    apiKey: env('TRANSAK_API_KEY'),
    environment: getNearEnv(nearEnv) === 'mainnet' ? 'PRODUCTION' : 'STAGING',
    widgetWidth: `500px`,
    widgetHeight: `600px`,
    themeColor: `#00C6A2`,
    hostURL: typeof window !== 'undefined' ? window.location.origin : ``,
    defaultCryptoCurrency: 'NEAR',
    cryptoCurrencyCode: 'NEAR',
    walletAddress: accountId || '',
  };
}

// Upstream served an orderbook-only site on a separate hostname. Not part of this fork.
export const isNewHostName = false;
